import React, { PureComponent, ReactNode, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import environment from "src/environment";
import { IWithCart } from "src/hoc/repository/withCart/withCart";
import { IWithMe } from "src/hoc/repository/withMe/withMe";
import { IWithAuthenticated } from "src/hoc/withAuthenticated/withAuthenticated";
import analytics from "src/lib/analytics";
import {
  AccountCreation,
  AccountLogin,
  AddressesEdition,
  Alphabetic,
  Authentication,
  Checkout,
  CMS,
  GiftCard,
  Gutter,
  Home,
  KitchenSink,
  MerchantGiftOperation,
  NewArrivals,
  OrderConfirmation,
  Payment,
  PaymentRedirect,
  Search,
  Shipping,
  StoreData,
  StoreLocatorPage,
} from "src/pages";
import {
  getLengowQuoteItems,
  getSkuList,
  isNewClient,
} from "src/services/analytics.service";
import { convertNumberToString } from "src/services/number.service";

import LoadingPage from "../Pages/LoadingPage";

interface IState {
  prevUrl: string | null;
}

export interface IProps extends IWithAuthenticated, IWithMe, IWithCart {}

class Routes extends PureComponent<IProps, IState> {
  public state: IState = {
    prevUrl: null,
  };

  public componentDidUpdate = (): void => {
    const { meLoading, cartLoading } = this.props;
    const currentUrl = window.location.pathname;

    if (currentUrl !== this.state.prevUrl && !meLoading && !cartLoading) {
      this.onRouteChanged();
      this.setState({ prevUrl: currentUrl });
    }
  };

  public onRouteChanged(): void {
    const { isAuthenticated, cart, me } = this.props;
    const createdAt = me?.createdAt;
    const items = cart?.items;
    const total = cart?.total;

    analytics.push({
      customerEmail: me?.email,
      customerGroup: me?.groupName,
      event: "page",
      isSecure: window.location.protocol === "https:" ? "1" : "0",

      lengowCustomerID: environment.ANALYTICS.LENGOW_CUSTOMER_ID,
      lengowGroupID: environment.ANALYTICS.LENGOW_GROUP_ID,
      lengowQuoteItems: getLengowQuoteItems(items || []),
      lengowTransactionItems: getSkuList(items || []),
      quoteAmount: convertNumberToString(total),
      quoteID: cart?.id,
      quoteItems: getSkuList(items || []),
      url: window.location.pathname,
      userAgent: "mobile",
      visitorCodeType: isNewClient({ isAuthenticated, createdAt })
        ? environment.ANALYTICS.NETAFFILIATION_NEW
        : environment.ANALYTICS.NETAFFILIATION_OLD,
      visitorID: me?.id,
      visitorLoginState: this.props.isAuthenticated ? "1" : "0",
    });
  }

  public render(): ReactNode {
    return (
      <Suspense fallback={<LoadingPage />}>
        <Switch>
          <Route exact path="/:lang(en)?/" component={Home} />
          <Route exact path="/:lang(en)?/checkout/cart" component={Checkout} />
          <Route
            exact
            path="/:lang(en)?/checkout/order/authentication/:mode"
            component={Authentication}
          />
          <Route
            exact
            path="/:lang(en)?/checkout/order/shipping"
            component={Shipping}
          />
          <Route
            exact
            path="/:lang(en)?/checkout/order/shipping/addresses"
            component={AddressesEdition}
          />
          <Route
            exact
            path="/:lang(en)?/checkout/order/shipping/addresses/billing"
            // tslint:disable-next-line:no-any
            render={(props: any): ReactNode => (
              <AddressesEdition {...props} open="billing" />
            )}
          />
          <Route
            exact
            path="/:lang(en)?/checkout/order/payment"
            component={Payment}
          />
          <Route
            exact
            path="/:lang(en)?/checkout/order/payment/redirect"
            component={PaymentRedirect}
          />
          <Route
            exact
            path={[
              "/:lang(en)?/be2bill/payments/return",
              "/:lang(en)?/dalenys/payments/return",
              "/:lang(en)?/paypal/standard/success",
            ]}
            component={OrderConfirmation}
          />
          <Route
            exact
            path={[
              "/:lang(en)?/be2bill/payments/cancel",
              "/:lang(en)?/dalenys/payments/cancel",
              "/:lang(en)?/paypal/standard/cancel",
            ]}
          >
            {({
              match: { params },
            }: {
              match: { params: { lang?: string } };
            }): JSX.Element => (
              <Redirect
                to={(params.lang ? "/" + params.lang : "") + "/checkout/cart"}
              />
            )}
          </Route>
          <Route exact path="/cms/:cmsType/:cmsId">
            {({
              match: { params },
            }: {
              match: { params: { cmsType?: string; cmsId?: string } };
            }): JSX.Element => (
              // @ts-ignore
              <CMS cmsId={params.cmsId} cmsType={params.cmsType} />
            )}
          </Route>
          <Route
            exact
            path="/:lang(en)?/catalogsearch/result/"
            component={Search}
          />
          <Route
            exact
            path="/:lang(en)?/customer/account/create"
            component={AccountCreation}
          />
          <Route
            exact
            path="/:lang(en)?/customer/account/login"
            component={AccountLogin}
          />
          <Route
            path="/:lang(en)?/customer/account/login/"
            component={AccountLogin}
          />
          <Route exact path="/:lang(en)?/kitchensink" component={KitchenSink} />
          <Route
            exact
            path={[
              "/:lang(en)?/store-locator",
              "/:lang(en)?/locator/",
              "/:lang(en)?/amlocator",
            ]}
            component={StoreLocatorPage}
          />
          <Route path={"/store-locator/:storeid"} component={StoreData} />
          <Route
            exact
            path={[
              "/selections/derniers-arrivages.html",
              "/en/selections/new-arrivals.html",
            ]}
            component={NewArrivals}
          />
          <Route exact path={["/marque.html", "/en/brands.html"]}>
            <Alphabetic pageName="brands" />
          </Route>
          <Route exact path={["/distillerie.html", "/en/distilleries.html"]}>
            <Alphabetic pageName="distilleries" />
          </Route>
          <Route
            exact
            path={["/pays-d-origine.html", "/en/origin-countries.html"]}
          >
            <Alphabetic pageName="countries" />
          </Route>
          <Route
            exact
            path={"/coupons-cavistes"}
            component={MerchantGiftOperation}
          />
          <Route
            exact
            path={["/cheque-cadeau-whisky-fr.html", "/en/gift-card.html"]}
            component={GiftCard}
          />
          <Route exact path="/carte-cadeau-whisky-fr.html">
            <Redirect to="/cheque-cadeau-whisky-fr.html" />
          </Route>
          <Route path="/" component={Gutter} />
        </Switch>
      </Suspense>
    );
  }
}

export default Routes;
